import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";
import _ from "lodash";

import Alert from "components/shared/Alert";
import ClosableDialog from "components/shared/ClosableDialog";
import { Para16Text } from "components/shared/Typography";
import { VERIFIED_USERS } from "helpers/apis";
import useAxios from "helpers/axios";
import { colors } from "theme";
import { IAssignedUser, OrgUser } from "types/user";

import { ITransferInfoLineProps, TransferInfoLine } from "./TransferInfoLine";

interface IOwnershipTransferDialogProps {
  onTransfer: (owner: OrgUser, setError: (error: string) => void) => void;
  openDialog: boolean;
  infos: ITransferInfoLineProps[];
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  listbox: {
    padding: 0,
  },
  groupLabel: {
    ...theme.typography.h6,
    padding: "6px 16px",
    color: colors.grey[300],
  },
  option: {
    fontSize: 14,
    lineHeight: "20.41px",
    fontWeight: 400,
    padding: "6px 16px !important",
    letterSpacing: 0.5,
  },
}));

export const OwnershipTransferDialog: React.FC<
  IOwnershipTransferDialogProps
> = ({ onTransfer, openDialog, infos, setOpenDialog }) => {
  const [users, setUsers] = useState<IAssignedUser[]>([]);
  const { loading } = useAxios({
    config: { url: VERIFIED_USERS },
    successCallback: ({ response }: any) =>
      setUsers(response.data as IAssignedUser[]),
  });
  const [newOwner, setNewOwner] = useState<OrgUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const classes = useStyles();

  useEffect(() => {
    if (openDialog) {
      setNewOwner(null);
      setError(null);
    }
  }, [openDialog]);

  const closeDialog = () => {
    setOpenDialog(false);
  };

  const options: Array<OrgUser> = useMemo(
    () =>
      Array.isArray(users)
        ? _.sortBy(
            users.map((user: IAssignedUser) => ({
              ...user,
              organization: user.email.split("@")[1],
            })),
            ["organization", "email"]
          )
        : [],
    [users]
  );

  return (
    <ClosableDialog
      maxWidth="sm"
      fullWidth
      open={openDialog}
      onClose={closeDialog}
    >
      <DialogTitle>
        <Typography variant="h1" component="h2">
          Change owner
        </Typography>
      </DialogTitle>

      <DialogContent>
        {!!error && (
          <Box mb={4}>
            <Alert
              severity="warning"
              icon={false}
              onClose={() => setError(null)}
            >
              An error has occurred. Please try again.
            </Alert>
          </Box>
        )}
        <Para16Text>
          To transfer ownership of the selected panel, search for a new owner in
          the field below.
        </Para16Text>
        <Box display="flex" flexDirection="column" rowGap={1} mt={4}>
          {infos.map((infoLine) => (
            <TransferInfoLine key={infoLine.label} {...infoLine} />
          ))}
          <Box mt={1}>
            <Autocomplete
              options={options}
              loading={loading}
              groupBy={(option) => option.organization}
              getOptionLabel={(option) => option.email}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by Email (tip: type last name or organization)"
                  variant="filled"
                  size="small"
                />
              )}
              renderGroup={({ children, group }) => (
                <Box component="li" key={group}>
                  <div
                    className={`MuiListSubheader-root MuiAutocomplete-groupLabel MuiListSubheader-sticky MuiListSubheader-gutters ${classes.groupLabel}`}
                  >
                    {options.find((option) => option.organization === group)
                      ?.companyName || group}
                  </div>
                  <Box component="ul" className="MuiAutocomplete-groupUl">
                    {children}
                  </Box>
                </Box>
              )}
              value={newOwner}
              onChange={(evt, newOwner: OrgUser | null) => {
                setNewOwner(newOwner);
              }}
              onFocus={() => setNewOwner(null)}
              popupIcon={<></>}
              classes={{
                groupLabel: classes.groupLabel,
                option: classes.option,
                listbox: classes.listbox,
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box
          display="flex"
          justifyContent="center"
          columnGap={2}
          pb={3}
          mt={-1}
          flex={1}
        >
          <Button
            onClick={() => onTransfer(newOwner!, setError)}
            size="large"
            disabled={!newOwner || loading}
          >
            Transfer panel ownership
          </Button>
          <Button variant="outlined" size="large" onClick={closeDialog}>
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </ClosableDialog>
  );
};
